/**
 * This is the entry file for the bundle we send to the browser.
 *
 * This only ever runs on the browser - never on the server. View the top-level
 * readme for more information on how the server and client interplay.
 */
import React from "react";
import ReactDOM from "react-dom";
import * as Integrations from "@sentry/integrations";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { loadableReady } from "@loadable/component";
import {
  ApolloClient,
  ApolloProvider,
  ApolloLink,
  createHttpLink,
  split
} from "@apollo/client";
import { InMemoryCache } from "@apollo/client/cache";
import { onError } from "@apollo/client/link/error";
import App from "./components/App";
import createStore, { createPersistor } from "./store";
import config from "./config";
import * as Sentry from "@sentry/browser";
import { isAuthError, getLoginRedirectUrl } from "./utils/auth";
import * as analytics from "./analytics";
import { getError } from "./utils/graphql";
import * as FullStory from "@fullstory/browser";

if (process.env.NODE_ENV !== "production" && config.FULLSTORY_ORG) {
  FullStory.init({ orgId: config.FULLSTORY_ORG });
}

Sentry.init({
  dsn: config.SENTRY_DSN,
  integrations: [new Integrations.Dedupe(), new Integrations.ExtraErrorData()],
  release: (window as any).__APP_VERSION__,
  whitelistUrls: [
    "https://storage.googleapis.com/echo-webapp/",
    "https://web.staging.echo.co.uk/",
    "https://web.echo.co.uk/",
    "https://web.staging.lloydsdirect.co.uk/",
    "https://web.lloydsdirect.co.uk/"
  ],
  ignoreErrors: ["ChunkLoadError", "instantSearchSDKJSBridgeClearHighlight"]
});

const apiLink = createHttpLink({
  uri: config.CLIENT_API_URL,
  credentials: "include",
  headers: {
    "X-Client-ID": "web",
    "X-Client-Version": (window as any).__APP_VERSION__
  },
  fetch: async (input: RequestInfo, init?: any) => {
    // types are a bit weird for init so typing as any and being defensive
    if (init && init.headers && typeof init.headers === "object") {
      init.headers["X-Device-ID"] = analytics.deviceId();
    }

    return fetch(input, init);
  }
});

// Create a seperate link for loading CMS data
const cmsLink = createHttpLink({
  uri: config.DATO_URL,
  headers: {
    Authorization: `Bearer ${config.DATO_TOKEN}`
  }
});

// Create a seperate link for loading CMS data
const productsLink = createHttpLink({
  uri: config.DATO_URL,
  headers: {
    Authorization: `Bearer ${config.PRODUCTS_TOKEN}`
  }
});

const httpLink = split(
  operation => operation.getContext().cms === true,
  cmsLink,
  split(
    operation => operation.getContext().products === true,
    productsLink,
    apiLink
  )
);

const errorLink = onError(({ operation, graphQLErrors }) => {
  if (isAuthError(graphQLErrors)) {
    const { location } = window;
    location.href = getLoginRedirectUrl(
      location.pathname + location.search,
      true
    );
    return;
  }

  if (!graphQLErrors) {
    return;
  }

  Sentry.withScope(scope => {
    scope.setTag("operationName", operation.operationName);

    const response = operation.getContext().response as Response | null;
    if (response) {
      const traceId = response.headers.get("x-trace-id");
      if (traceId) {
        scope.setTag("traceId", traceId);
      }
    }
    Sentry.captureException(getError(graphQLErrors));
  });
});

// Ordering is important here, errorLink needs to be first.
const apolloLink = ApolloLink.from([errorLink, httpLink]);

export const apolloClient = new ApolloClient({
  link: apolloLink,
  cache: new InMemoryCache().restore((window as any).__APOLLO_STATE__)
});

// Injected by the first server render, this is the redux state that was the result
// of the initial render. Here, we get it out and then delete it off the window
// so it can be garbage collected. This is recommended by the redux docs (the deleting).
const preloadedState = (window as any).__REDUX_STATE__;
delete (window as any).__REDUX_STATE__;

const didServerRender = (window as any).__SERVER_RENDERED__;

const store = createStore(preloadedState);

const persistor = createPersistor(store);

// Set the store in the analytics module to be able to access the intercomId
analytics.setStore(store);

if (process.env.NODE_ENV !== "production") {
  var axe = require("react-axe");
  axe(React, ReactDOM, 1000);
}

if (config.MOCK_API === "true") {
  require("../mocks");
}

// Since we use SSR, rendering a PersistGate (the recommended redux-persist implementation)
// in the client would cause a DOM-mismatch between the server and the client, instead we
// subscribe to the persistor and wait until it is 'bootstrapped' and then rehydrate, which
// achieves the same effect https://github.com/rt2zz/redux-persist/issues/1008#issuecomment-474083851
persistor.subscribe(() => {
  // Hydrate React tree when persistor has synced with redux store
  const { bootstrapped } = persistor.getState();

  if (bootstrapped) {
    // Initialise the loadable-components library. This is so that it can load
    // the components that are required to hydrate the server render. The other
    // half of this is in app/server.tsx where we store the server
    // loadable-components state into a global on the window.
    loadableReady().then(() => {
      const tree = (
        <BrowserRouter>
          <Provider store={store}>
            <ApolloProvider client={apolloClient}>
              <App />
            </ApolloProvider>
          </Provider>
        </BrowserRouter>
      );

      if (didServerRender) {
        ReactDOM.hydrate(tree, document.getElementById("app"));
      } else {
        ReactDOM.render(tree, document.getElementById("app"));
      }
    });
  }
});
